"use client";

import React from "react"
import {Locale} from "@/i18n-config";
import Image from "next/image";
import type {ContentBox as ContentBoxProps} from "@docid/types/src/payload-types";
import {RichText} from "@/components/content/common/RichText";
import {ContentLink} from "@/components/content/blocks/ContentLink/Component";
import {getMediaBySize, isMedia} from "@/utils/content/media";
import clsx from "clsx";

type Props = {
    lang: Locale;
    dictionary: any;
    className?: string;
    block: ContentBoxProps;
    position?: number;
};

export function ContentBox({ lang, dictionary, className, block, position } : Props) {
    return (
        <div className={className}>
            <div className={clsx(block.style === "image-left-content-right" ? "lg:flex-row" : "lg:flex-row-reverse", "flex flex-col lg:flex-row gap-8 md:gap-12 items-center lg:items-start justify-center px-4 pt-4 pb-0 md:px-8 md:pt-8 md:pb-4 rounded-3xl bg-white")}>
                {block.image && isMedia(block.image) && (
                    <div className="w-full lg:w-1/2 pb-0 md:pb-4">
                        <div className="flex items-center justify-center shadow-inner rounded-xl overflow-hidden w-full">
                            {(() => {
                                const image = getMediaBySize(block.image, "medium");
                                return (
                                    <Image
                                        style={{ objectFit: "cover" }}
                                        width={image.width}
                                        height={image.height}
                                        src={image.url}
                                        alt={image.alt}
                                        priority={!!(position && position <= 2)}
                                    />
                                );
                            })()}
                        </div>
                    </div>
                )}
                <div className="flex flex-col gap-5 md:gap-6 lg:gap-8 px-4 md:px-0 w-full lg:w-1/2 pb-6 md:pb-8">
                    {block.title && <h2 className="font-bold text-2xl md:text-3xl leading-snug uppercase text-zinc-900 text-center md:text-left">{block.title}</h2>}
                    {block.description && <div className="text-lg md:text-xl text-center md:text-left text-normal leading-normal whitespace-pre-wrap">{block.description}</div>}
                    {block.content && (
                        <div className="text-center md:text-left whitespace-pre-wrap !text-zinc-900">
                            <RichText data={block.content} theme={"light"} />
                        </div>
                    )}
                    {block.list && block.list.length > 0 && (
                        <div className="flex flex-col gap-4 md:gap-8 mt-4 md:mt-0">
                            {block.list.map((item, index) => (
                                <div key={`item-${index}`} className="flex flex-col md:flex-row gap-6 items-center justify-center">
                                    {item.image && isMedia(item.image) && (
                                        <div className="w-24 shrink-0 grow-0 flex items-center justify-center">
                                            {(() => {
                                                const image = getMediaBySize(item.image, "medium");
                                                return (
                                                    <Image
                                                        style={{ objectFit: "fill" }}
                                                        width={image.width}
                                                        height={image.height}
                                                        src={image.url}
                                                        alt={image.alt}
                                                        priority={!!(position && position <= 2)}
                                                    />
                                                );
                                            })()}
                                        </div>
                                    )}
                                    {item.content && (
                                        <div className="grow">
                                            <RichText data={item.content} className="text-lg md:text-xl text-center md:text-left text-normal leading-normal whitespace-pre-wrap" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {block.buttons && block.buttons.length > 0 && (
                        <div className="flex flex-row flex-wrap items-center justify-center md:justify-start gap-4">
                            {block.buttons.map((button, index) => (
                                <ContentLink
                                    key={`${block.id}-button-${index}`}
                                    lang={lang}
                                    dictionary={dictionary}
                                    block={button}
                                    className="bg-black hover:bg-black/80 animate-pulseBgBlack inline-flex items-center justify-center gap-2 md:min-w-52 px-6 py-3 rounded-md text-center font-jost font-semibold outline-2 outline-offset-2 transition-colors text-lg text-white select-none" />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

}